/* 216 addendum 5 §9 [RULED 80] — THE LIBRARY IMPORT'S PREVIEW, BEFORE ANYTHING
   IS WRITTEN. Laurent, 14/10: « the import should upsert (match on ISBN,
   falling back to CODE/title) instead of blindly inserting, and ideally offer a
   "replace entire library" option ». The server plans the import
   (routes/books.js POST /import, lib/bookImport.js) and this window shows the
   plan: how many rows add a book, update one, change nothing, or are ambiguous
   — the ambiguous listed with their reason and not imported until resolved
   (ruling 63, never guessed). « Replace the whole library » [RULED 80], in the
   same window: a second mode (214's « Remplacer tous les dossiers » shape — a
   radio, the word « destructive » beside it, the button red) that also removes
   the books the file does not list, SHOWN FIRST; a book on loan or holding a
   PDF is not removed that way — listed and kept [ADVISOR]. Confirming imports.
   The `data-lib-import-*` hooks are the drive's (scripts/drive-216.js
   --part=library). */
import React from "react";
import { ModalLayout } from "./SharedUI";
import { FS } from "../lib/tokens";
import type { LibraryImportPlan } from "../api";
import type { Translate } from "../lib/interestRefusal"; // the translator, typed where it is called (209 §7)

export type LibraryImportMode = "apply" | "replace";

const LIST: React.CSSProperties = { margin: "4px 0 10px", paddingLeft: 18, fontSize: FS.s10_5, maxHeight: 160, overflowY: "auto" };
const HEAD: React.CSSProperties = { fontSize: FS.s10, fontWeight: 600, color: "var(--tm)", marginBottom: 4 };

export default function LibraryImportModal({
  plan,
  mode,
  setMode,
  busy,
  onClose,
  onApply,
  t,
}: Readonly<{
  plan: LibraryImportPlan;
  mode: LibraryImportMode;
  setMode: React.Dispatch<LibraryImportMode>;
  busy: boolean;
  onClose: () => void;
  onApply: () => void;
  t: Translate;
}>) {
  const c = plan.counts;
  const removing = plan.unlisted.filter((u) => u.removable);
  const kept = plan.unlisted.filter((u) => !u.removable);
  // nothing to write: no row adds or updates, and (in the replace mode) nothing to remove
  const nothing = c.add + c.update === 0 && (mode !== "replace" || removing.length === 0);
  const bookLine = (u: { id: number; title: string | null; author: string | null }) =>
    ["#" + String(u.id), u.title ?? "", u.author ?? ""].filter(Boolean).join(" — ");
  return (
    <ModalLayout
      title={t("library.import.title", { fallback: "Import the library" })}
      onClose={onClose}
      showX={false}
      maxWidth={640}
      footer={
        <div className="modal-footer">
          <button type="button" className="modal-cancel" onClick={onClose} disabled={busy}>
            {t("common.cancel", { fallback: "Cancel" })}
          </button>
          {/* the red arm is 214's « Remplacer tous les dossiers » shape (modal-delete); the add arm the house's save */}
          <button
            type="button"
            className={mode === "replace" ? "modal-delete" : "modal-save"}
            data-lib-import-go={mode}
            disabled={busy || nothing}
            onClick={onApply}
          >
            {mode === "replace"
              ? t("library.import.replace_all", { fallback: "Replace the whole library" })
              : `✓ ${t("common.import", { fallback: "Import" })}`}
          </button>
        </div>
      }
    >
      <p data-lib-import-counts="" style={{ fontSize: FS.s11, marginBottom: 8 }}>
        {t("library.import.counts", {
          add: c.add,
          update: c.update,
          unchanged: c.unchanged,
          ambiguous: c.ambiguous,
          fallback: `To add: ${String(c.add)} · To update: ${String(c.update)} · Unchanged: ${String(c.unchanged)} · Ambiguous: ${String(c.ambiguous)}`,
        })}
      </p>
      {plan.ambiguous.length > 0 && (
        <div>
          <div style={{ ...HEAD, color: "var(--am)" }}>
            {t("library.import.ambiguous_intro", { fallback: "Ambiguous rows — not imported until resolved:" })}
          </div>
          <ul data-lib-import-ambiguous="" style={LIST}>
            {plan.ambiguous.map((a) => (
              <li key={a.index}>
                {t(a.reason === "rows" ? "library.import.amb_rows" : "library.import.amb_books", {
                  row: a.row,
                  title: a.title,
                  ids: a.bookIds.join(", #"),
                  fallback: a.reason === "rows"
                    ? `Row ${String(a.row)} “${a.title}”: several rows name the same book (#${a.bookIds.join(", #")})`
                    : `Row ${String(a.row)} “${a.title}”: several books match (#${a.bookIds.join(", #")})`,
                })}
              </li>
            ))}
          </ul>
        </div>
      )}
      {nothing && mode !== "replace" && (
        <p data-lib-import-nothing="" style={{ fontSize: FS.s11, color: "var(--tm)", marginBottom: 8 }}>
          {t("library.import.nothing_to_write", { fallback: "Nothing to write: the library matches the file." })}
        </p>
      )}
      <div style={{ marginBottom: 10 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: FS.s11, cursor: "pointer", marginBottom: 4 }}>
          <input type="radio" data-lib-import-mode="apply" checked={mode === "apply"} onChange={() => { setMode("apply"); }} disabled={busy} />
          {t("library.import.mode_add", { fallback: "Add and update" })}
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: FS.s11, cursor: "pointer" }}>
          <input type="radio" data-lib-import-mode="replace" checked={mode === "replace"} onChange={() => { setMode("replace"); }} disabled={busy} />
          <span>
            {t("library.import.replace_all", { fallback: "Replace the whole library" })}{" "}
            <span style={{ color: "var(--rd)", fontSize: FS.s10 }}>
              {t("app.import.destructive", { fallback: "(destructive)" })}
            </span>
          </span>
        </label>
      </div>
      {mode === "replace" && (
        <div data-lib-import-removals="">
          <div style={{ ...HEAD, color: "var(--rd)" }}>
            {t("library.import.replace_removes", { fallback: "Not in the file — removed by the replacement:" })}
          </div>
          {removing.length > 0 ? (
            <ul data-lib-import-removing="" style={LIST}>
              {removing.map((u) => (
                <li key={u.id}>{bookLine(u)}</li>
              ))}
            </ul>
          ) : (
            <p style={{ fontSize: FS.s10_5, color: "var(--tm)", margin: "4px 0 10px" }}>
              {t("library.import.replace_none", { fallback: "No book to remove: the file lists them all." })}
            </p>
          )}
          {kept.length > 0 && (
            <>
              <div style={HEAD}>
                {t("library.import.replace_kept", { fallback: "Not in the file but kept (on loan, or holding a PDF):" })}
              </div>
              <ul data-lib-import-kept="" style={LIST}>
                {kept.map((u) => (
                  <li key={u.id}>
                    {bookLine(u)}
                    {" · "}
                    {u.keptFor === "loan"
                      ? t("library.import.kept_loan", { fallback: "on loan" })
                      : t("library.import.kept_pdf", { fallback: "PDF" })}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </ModalLayout>
  );
}
